import React from 'react';
import { Form, Input, Select } from 'antd';
import type { FormInstance } from 'antd';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import W9UploadDesc from 'components/W9UploadDesc';
import AutoAddressInput from 'components/AutoAddressInput';
import { lookupNpiStandalone } from 'api/common';
import NpiRegistryBox from 'pages/InsuranceBillingOnboarding/components/NpiRegistryBox';
import { ENTITY_TYPE_OPTIONS, ENTITY_TYPE_OTHER } from '../../constants';
import type { PracticeEnrollment } from 'types/credentialing';
import s from './s.module.less';

interface PracticeEnrollmentFieldsProps {
    // Needed to watch entityType so the "Other" free-text field can reveal itself.
    form: FormInstance<PracticeEnrollment>;
    // When true, every field is required (the review page blocks Save changes on any blank).
    // Onboarding leaves it off so a partially-filled practice can still be saved and resumed.
    required?: boolean;
    // NPI 结果框的外层类名：两个宿主的字段间距口径不同（onboarding 表单是 Form.Item 16px 下边距，
    // review 内联编辑是 flex gap），间距由宿主自己给，组件不猜
    npiBoxClassName?: string;
}

const requiredRule = (message: string) => ({ required: true, message });

// The six group-enrollment fields (legal name, group NPI, tax id, entity type, location, W-9),
// shared by the onboarding Practice-setup step and the review page's inline edit. The Form
// wrapper, initialValues and onValuesChange (including clearing entityTypeOther on switch) live
// with each consumer — this component only owns the fields and their validation.
const PracticeEnrollmentFields: React.FC<PracticeEnrollmentFieldsProps> = ({
    form,
    required = false,
    npiBoxClassName,
}) => {
    const entityType = Form.useWatch('entityType', form);
    const isOther = entityType === ENTITY_TYPE_OTHER;

    return (
        <>
            <Form.Item
                name="legalBusinessName"
                label="Legal business name"
                rules={required ? [requiredRule('Legal business name is required')] : undefined}
            >
                <Input />
            </Form.Item>

            <div className={s.fieldRow}>
                {/* 位数/查询结果一律由下方的 NpiRegistryBox 出（与 insurance billing 一致），
                    这里只留必填，否则同一条错误会以 antd 红字 + 结果框两种形式重复出现 */}
                <Form.Item
                    name="groupNpi"
                    label="Group NPI (Type 2)"
                    className={s.rowItem}
                    rules={required ? [requiredRule('Group NPI is required')] : undefined}
                >
                    <Input placeholder="10 digit NPI for the practice" />
                </Form.Item>
                <Form.Item
                    name="taxId"
                    label="Tax ID / EIN"
                    className={s.rowItem}
                    rules={[
                        ...(required ? [requiredRule('Tax ID / EIN is required')] : []),
                        {
                            // 这个字段 EIN（XX-XXXXXXX）和个人 SSN（XXX-XX-XXXX）都可能填，
                            // 报错文案不能只写 EIN 的格式，统一成不带格式的位数提示
                            validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                return /^\d{2}-?\d{7}$/.test(value.replace(/-/g, ''))
                                    ? Promise.resolve()
                                    : Promise.reject('Must be 9 digits');
                            },
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
            </div>

            {/* NPI Registry 校验：practice 必须登记为机构 NPI（Type 2）。
                姓名不比对——NPPES 上的 organization name 与 legal business name 常因 DBA/简写不一致。
                查询走 api/common 的独立端点（credentialing 用户不一定开通了 Insurance Billing），
                practice 表单不收 taxonomy，关掉下拉 */}
            <NpiRegistryBox
                ns={[]}
                npiKey="groupNpi"
                expectType="Type 2"
                showTaxonomy={false}
                className={npiBoxClassName}
                lookup={lookupNpiStandalone}
            />

            <div className={s.fieldRow}>
                <Form.Item
                    name="entityType"
                    label="Entity type"
                    className={s.rowItem}
                    rules={required ? [requiredRule('Entity type is required')] : undefined}
                >
                    <Select className={s.select} options={ENTITY_TYPE_OPTIONS.map((o) => ({ label: o, value: o }))} />
                </Form.Item>
                <Form.Item
                    name="practiceLocation"
                    label="Practice location"
                    className={s.rowItem}
                    rules={required ? [requiredRule('Practice location is required')] : undefined}
                >
                    <AutoAddressInput placeholder="Street address" displayFields={['street', 'city', 'state']} />
                </Form.Item>
            </div>

            {isOther && (
                <Form.Item
                    name="entityTypeOther"
                    label="Please specify your entity type"
                    rules={required ? [requiredRule('Please specify your entity type')] : undefined}
                >
                    <Input placeholder="e.g. Nonprofit corporation" />
                </Form.Item>
            )}

            {/* 标题 + 说明 + 上传框是一组：包成一个整体，宿主是 flex + gap 时（review 页的
                Practice enrollment 面板）才不会被逐个拉开 24px，组内维持自身的 8px 节奏 */}
            <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
                    W-9 (group Tax ID)
                </div>
                <W9UploadDesc>
                    Please upload your practice&apos;s completed and signed W-9 form, used for the group&apos;s Tax ID
                    / EIN.
                </W9UploadDesc>
                <Form.Item name="w9FileName" rules={required ? [requiredRule('W-9 is required')] : undefined}>
                    <FileUploadPrivate uploadType="LICENSE" accept="application/pdf" tips="PDF up to 30MB" />
                </Form.Item>
            </div>
        </>
    );
};

export default PracticeEnrollmentFields;
